import './bootstrap'
import * as Sentry from '@sentry/browser'
import { redactTenantExportSecrets } from './global/tenantExportPrivacy.js'

import { Livewire, Alpine } from '../../vendor/livewire/livewire/dist/livewire.esm'
import initResourcerer from '../../vendor/lacodix/resourcerer/resources/js/resourcerer.js'
import initRichText, { registerRichTextExtension } from '../../vendor/lacodix/resourcerer-rich-text/resources/js/rich-text.js'
import { createMeetingInlineTasks, meetingTaskCommands } from './components/meetingInlineTasks.js'
import initResourcererTags from '../../vendor/lacodix/resourcerer-tags/resources/js/tags.js'
import letterheadEditor from './components/letterheadEditor.js'
import placeholderPicker from './components/placeholderPicker.js'
import animatedList from './components/animatedList.js'
import Theme from './components/theme.js'
import DropZone from './global/dropzone'
import subnavScroller from './components/subnavScroller.js'
import pushToggle from './components/pushToggle.js'
import hideHeader from './components/hideHeader.js'
import infoBanner from './components/infoBanner.js'
import rosterDnd from './components/rosterDnd.js'
import personOrFreeTextPicker from './components/personOrFreeTextPicker.js'
import resourceAvailabilityAllDay from './components/resourceAvailabilityAllDay.js'
import rosterPersonPicker from './components/rosterPersonPicker.js'
import rosterCirclePicker from './components/rosterCirclePicker.js'
import inlineEditable from './components/inlineEditable.js'
import meetingEditor from './components/meetingEditor.js'
import pointExcerpt from './components/pointExcerpt.js'
import pointHistory from './components/pointHistory.js'
import meetingAttendancePicker from './components/meetingAttendancePicker.js'
import meetingSeriesCode from './components/meetingSeriesCode.js'
import tabs from './components/tabs.js'
import timeCombobox from './components/timeCombobox.js'
import meetingTimeRange from './components/meetingTimeRange.js'
import pollDateOptions from './components/pollDateOptions.js'
import pollDraftAutosave from './components/pollDraftAutosave.js'
import fixedMenu from './components/fixedMenu.js'
import longPressAction from './components/longPressAction.js'
import autoSubmit from './components/autoSubmit.js'
import taskBoardKanban from './components/taskBoardKanban.js'
import taskAssigneePicker from './components/taskAssigneePicker.js'
import storedTaskBoardWorkspace from './components/storedTaskBoardWorkspace.js'
import registrationResendCountdown from './components/registrationResendCountdown.js'
import memberHomeContext from './components/memberHomeContext.js'
import eventOverview, { eventOverviewTable } from './components/eventOverview.js'
import rosterOverview, { rosterOverviewTable } from './components/rosterOverview.js'
import { createMemberOrientation } from './global/memberOrientation'
import 'driver.js/dist/driver.css'
import registrationAltcha from './components/registrationAltcha.js'
import channelConversation from './components/channelConversation.js'
import notifications, { clearNotificationsWithoutHost } from './components/notifications.js'
import { Capacitor } from '@capacitor/core'
import { initNativePush } from './native/push'
import { initNativeDownloads } from './native/downloads'

import guardLivewireStart from './global/livewireStartGuard'
import { initMemberLetterFollowup } from './global/memberLetterFollowup'
import { initTaskQuickView } from './global/taskQuickView'
import startVersionDriftGuard from './global/versionDriftGuard'
import { startBicAutocomplete } from './global/bicAutocomplete'
import { startEcho } from './global/echo'
import { calculateScrollAnchor } from './global/subnav'

// WebAuthn modules
import PasskeysManager from './components/passkeysManager'
import PasskeyLogin from './components/passkeyLogin'
import PasskeyConfirm from './components/passkeyConfirm'

const livewireBootDiagnostics = Object.freeze({
  configPresentAtModuleBoot: typeof window.livewireScriptConfig !== 'undefined',
  livewireAutoStartArmed: Alpine.__fromLivewire === true,
  documentReadyStateAtModuleBoot: document.readyState,
  navigationType: window.performance?.getEntriesByType?.('navigation')?.[0]?.type ?? null,
  serviceWorkerControlled: Boolean(navigator.serviceWorker?.controller),
  appVersion: document.querySelector('meta[name="app-version"]')?.content ?? null
})

const reportSuppressedDuplicateLivewireStart = () => {
  console.warn('[MEMBERGY-B5] Livewire duplicate start suppressed')

  if (!window.sentryDsn) {
    return
  }

  Sentry.captureMessage('Livewire duplicate start suppressed', {
    level: 'warning',
    fingerprint: ['MEMBERGY-B5', 'livewire-duplicate-start-suppressed'],
    tags: {
      'membergy.issue': 'MEMBERGY-B5'
    },
    extra: {
      ...livewireBootDiagnostics,
      documentReadyStateAtDuplicateStart: document.readyState
    }
  })
}

// The observed production callback and Membergy both dereference this shared export.
guardLivewireStart(Livewire, {
  onDuplicateStart: reportSuppressedDuplicateLivewireStart
})

import.meta.glob(['../images/**'])

/**
 * Detects a Livewire request-lifecycle rejection.
 *
 * Livewire surfaces a failed or aborted `/livewire/update` request as a
 * rejected plain object of shape `{ status, body, json, errors }` (no JS
 * stack). The common, benign case is a request that was superseded/aborted by
 * a `wire:navigate` redirect – e.g. the "cancel"/back button, which goes
 * through the server on purpose (`navigateBack` → `redirect()`). Those are not
 * actionable client-side.
 *
 * @param {unknown} value The captured promise-rejection value.
 * @returns {boolean}
 */
function isLivewireRequestRejection(value) {
  return !!value &&
    typeof value === 'object' &&
    !(value instanceof Error) &&
    'status' in value &&
    'json' in value &&
    'body' in value
}

// Sentry Browser SDK
if (window.sentryDsn) {
  const user = window.user ?? null
  const tenant = window.tenant ?? null
  const replayActive = user?.replay_active === true
  const fullTraceActive = user?.sentry_full_tracing === true

  Sentry.init({
    dsn: window.sentryDsn,
    tunnel: '/sentry-tunnel',
    environment: window.sentryEnvironment ?? import.meta.env.MODE,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true
      })
    ],
    tracesSampleRate: fullTraceActive ? 1.0 : 0.1,
    replaysSessionSampleRate: replayActive ? 1.0 : 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Drop non-actionable Livewire request rejections (aborted navigation,
      // redirects, validation/CSRF). Genuine server errors (>= 500) still pass
      // through and are also captured server-side with a full stack trace.
      const exception = hint?.originalException

      if (isLivewireRequestRejection(exception) && !(Number(exception.status) >= 500)) {
        return null
      }

      return redactTenantExportSecrets(event)
    }
  })

  if (user?.id) {
    Sentry.setUser({ id: user.id })
  }

  if (tenant?.id) {
    Sentry.setContext('tenant', { id: tenant.id, name: tenant.name })
    Sentry.setTag('tenant.id', tenant.id)
    Sentry.setTag('tenant.name', tenant.name)
  }

  Sentry.setTag('replay_active', replayActive)
}

window.calculateScrollAnchor = calculateScrollAnchor

startEcho()

/**
 * Lazy Alpine component loader.
 *
 * Registers an Alpine component whose implementation is loaded on demand via a
 * dynamic import. The chunk is only fetched when an element with the matching
 * x-data name actually appears in the DOM.
 *
 * Lifecycle forwarding:
 *   - init()    – called after the chunk is loaded and the component is merged
 *                 onto `this`. Safe when the component has no init().
 *   - destroy() – forwarded to the loaded component's destroy() if present.
 *                 Safe when the chunk never finished loading or the component
 *                 has no destroy().
 *
 * @param {() => Promise<{ default: Function }>} loader Dynamic import thunk.
 * @param {object} initialState Initial reactive Alpine state before the chunk loads.
 * @returns {(...args: unknown[]) => object} Alpine data factory.
 */
function lazyAlpineData(loader, initialState = {}) {
  return (...args) => {
    let _destroy = null

    return {
      ...initialState,
      async init() {
        let mod
        try {
          mod = await loader()
        } catch (err) {
          console.error('[lazyAlpineData] Failed to load component chunk:', err)
          return
        }

        const factory = mod.default
        const component = factory(...args)

        const componentInit = component.init
        const componentDestroy = component.destroy

        Object.assign(this, component)

        if (typeof componentDestroy === 'function') {
          _destroy = componentDestroy
        }

        if (typeof componentInit === 'function') {
          await componentInit.call(this)
        }
      },

      destroy() {
        if (typeof _destroy === 'function') {
          try {
            _destroy.call(this)
          } catch (err) {
            console.error('[lazyAlpineData] Error in component destroy():', err)
          }
        }
      }
    }
  }
}

/**
 * Generic lazy factory wrapper for Alpine components used via inline x-data spread.
 *
 * Unlike lazyAlpineData (which registers named Alpine.data components), this helper
 * is for factory functions called directly in Blade templates:
 *
 *   x-data="{ ...window.someFactory(...args), extraProp: value }"
 *   x-init="triggerMethod(...)"
 *
 * The chunk is only fetched when the trigger method is called (if triggerMethod is set),
 * or in init() otherwise. Properties declared in initialState are available reactively
 * before the chunk loads.
 *
 * Lifecycle forwarding:
 *   - The trigger method (first method called after load) invokes component.init() if present.
 *   - destroy() is forwarded to the loaded component's destroy() if present.
 *
 * @param {() => Promise<{ default: Function }>} loader        Dynamic import thunk.
 * @param {object}                                 initialState  Reactive Alpine state available before the chunk loads.
 * @param {string|null}                            triggerMethod If set, the chunk is loaded when this method is called
 *                                                               instead of in init(). The real method on the loaded
 *                                                               component is then invoked with the same arguments.
 * @returns {(...args: unknown[]) => object}  Factory returning an Alpine-compatible data object.
 */
function lazyAlpineFactory(loader, initialState = {}, triggerMethod = null) {
  return (...args) => {
    let _destroy = null

    const wrapper = {
      ...initialState,

      destroy() {
        if (typeof _destroy === 'function') {
          try {
            _destroy.call(this)
          } catch (err) {
            console.error('[lazyAlpineFactory] Error in component destroy():', err)
          }
        }
      }
    }

    const loadAndMerge = async function(triggerArgs = []) {
      let mod
      try {
        mod = await loader()
      } catch (err) {
        console.error('[lazyAlpineFactory] Failed to load component chunk:', err)
        return
      }

      const factory = mod.default
      const component = factory(...args)

      const componentInit = component.init
      const componentDestroy = component.destroy

      Object.assign(this, component)

      if (typeof componentDestroy === 'function') {
        _destroy = componentDestroy
      }

      if (typeof componentInit === 'function') {
        await componentInit.call(this)
      }

      if (triggerMethod && typeof this[triggerMethod] === 'function') {
        await this[triggerMethod](...triggerArgs)
      }
    }

    if (triggerMethod) {
      wrapper[triggerMethod] = async function(...triggerArgs) {
        await loadAndMerge.call(this, triggerArgs)
      }
    } else {
      wrapper.init = async function() {
        await loadAndMerge.call(this)
      }
    }

    return wrapper
  }
}

initResourcerer(Alpine)
initResourcererTags(Alpine)
// Registers `richTextEditor` and `richTextEmbedConsent`, the Alpine components
// behind resourcerer-rich-text's editor. The package is a direct dependency:
// without this registration the toolbar still renders server side while the
// editable surface stays dead and the console reports "richTextEditor is not defined".
registerRichTextExtension('meeting-inline-tasks', createMeetingInlineTasks, { commands: meetingTaskCommands })
initRichText(Alpine)
Alpine.data('theme', Theme)
Alpine.data('dropzone', DropZone)
Alpine.data('letterheadEditor', letterheadEditor)
Alpine.data('placeholderPicker', placeholderPicker)
Alpine.data('animatedList', animatedList)
Alpine.data('subnavScroller', subnavScroller)
Alpine.data('pushToggle', pushToggle)
Alpine.data('hideHeader', hideHeader)
Alpine.data('infoBanner', infoBanner)
Alpine.data('rosterDnd', rosterDnd)
Alpine.data('personOrFreeTextPicker', personOrFreeTextPicker)
Alpine.data('resourceAvailabilityAllDay', resourceAvailabilityAllDay)
Alpine.data('rosterPersonPicker', rosterPersonPicker)
Alpine.data('rosterCirclePicker', rosterCirclePicker)
Alpine.data('inlineEditable', inlineEditable)
Alpine.data('meetingEditor', meetingEditor)
Alpine.data('pointExcerpt', pointExcerpt)
Alpine.data('pointHistory', pointHistory)
Alpine.data('meetingAttendancePicker', meetingAttendancePicker)
Alpine.data('meetingSeriesCode', meetingSeriesCode)
Alpine.data('tabs', tabs)
Alpine.data('timeCombobox', timeCombobox)
Alpine.data('meetingTimeRange', meetingTimeRange)
Alpine.data('pollDateOptions', pollDateOptions)
Alpine.data('pollDraftAutosave', pollDraftAutosave)
Alpine.data('fixedMenu', fixedMenu)
Alpine.data('longPressAction', longPressAction)
Alpine.data('autoSubmit', autoSubmit)
Alpine.data('taskBoardKanban', taskBoardKanban)
Alpine.data('taskAssigneePicker', taskAssigneePicker)
Alpine.data('storedTaskBoardWorkspace', storedTaskBoardWorkspace)
Alpine.data('registrationResendCountdown', registrationResendCountdown)
Alpine.data('memberHomeContext', memberHomeContext)
Alpine.data('eventOverview', eventOverview)
Alpine.data('eventOverviewTable', eventOverviewTable)
Alpine.data('rosterOverview', rosterOverview)
Alpine.data('rosterOverviewTable', rosterOverviewTable)
Alpine.data('registrationAltcha', registrationAltcha)
Alpine.data('channelConversation', channelConversation)
Alpine.data('notifications', notifications)
document.addEventListener('livewire:navigated', clearNotificationsWithoutHost)
window.addEventListener('pageshow', clearNotificationsWithoutHost)

// Register WebAuthn Alpine components
Alpine.data('passkeysManager', PasskeysManager)
Alpine.data('passkeyLogin', PasskeyLogin)
Alpine.data('passkeyConfirm', PasskeyConfirm)

// lazy loaded Alpine components
// The hint and resource-change listener bind before the calendar chunk arrives.
Alpine.data('calendar', lazyAlpineData(() => import('./global/calendar'), {
  listViewActive: false,
  prepareResourceContextChange() {}
}))
Alpine.data('imageCropper', lazyAlpineData(() => import('./global/imageCropper'), {
  cropper: null
}))
Alpine.data('zxingScanner', lazyAlpineData(() => import('./components/zxingScanner'), {
  devices: [],
  error: null,
  deviceId: null,
  running: false,
  starting: false,
  stopping: false,
  hardStop() {},
  stop() {},
  start() {},
  refreshDevices() {},
  switchCamera() {}
}))

window.editorJs = lazyAlpineFactory(
  () => import('./global/editorjs'),
  { editor: null, dirty: false },
  'initEditor'
)
window.menuEditor = lazyAlpineFactory(() => import('./global/menuEditor'), {
  menuItems: [],
  output: '',
  selectedItem: null
})

// Shared in-flight lock for the event reply buttons (accept/tentative/reject).
// Set by longPressAction on the first tap, released on the triggered commit's
// settle (below). Prevents the slow-series double-tap 403 by disabling every
// reply button while a reply request is in flight. Registered before
// Livewire.start() so it exists when the buttons bind to $store.eventReplyLock.
Alpine.store('eventReplyLock', {
  busy: false
})

// Reload stale pages after a deploy before Livewire morphs new markup into
// old Alpine scopes (must be registered before Livewire.start()).
startVersionDriftGuard(Livewire)

window.memberOrientation = createMemberOrientation()

Livewire.start()
initTaskQuickView(Livewire)
initMemberLetterFollowup(Livewire)

// Release the reply lock once the reply request settles. An instant reply
// action is a single commit to the ActionDialog component whose slow work
// (creating/freezing the series occurrences) runs inside it, so the lock stays
// held for the whole round-trip and only opens back up here on success or error.
Livewire.hook('commit', ({ succeed, fail }) => {
  const lock = Alpine.store('eventReplyLock')

  if (!lock?.busy) {
    return
  }

  const release = () => {
    lock.busy = false
  }

  succeed(release)
  fail(release)
})

// Notify the roster DnD component after every Livewire round-trip so it can rebuild
// its SortableJS instances against the freshly rendered matrix DOM.
Livewire.hook('commit', ({ succeed }) => {
  // succeed fires after onRender (respond runs already at onFinish, before the
  // DOM morph) — defer one microtask so the matrices are freshly rendered before
  // rosterDnd rebuilds its SortableJS instances against them.
  succeed(() => queueMicrotask(() => {
    window.dispatchEvent(new CustomEvent('roster-dnd-refresh'))
  }))
})

startBicAutocomplete()

// Initialize Capacitor native push listeners when running inside a native shell
if (Capacitor.isNativePlatform()) {
  try {
    initNativePush()
  } catch (e) {
    console.error('Failed to init native push', e)
  }

  try {
    initNativeDownloads()
  } catch (e) {
    console.error('Failed to init native downloads', e)
  }
}

if ('serviceWorker' in navigator && window.location.origin !== 'http://membergy.test') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/build/sw.js', { scope: '/' })
      .then((registration) => {
        // optional: hier später Update-UI etc.
        // console.log('SW registered', registration)
      })
      .catch((error) => {
        console.error('SW registration failed', error)
      })
  })
}

if (typeof window !== 'undefined') {
  window.addEventListener('offline', () => {
    window.location.reload()
  })
}

function applyNativeAppTags() {
  if (!Capacitor.isNativePlatform()) return

  document.documentElement.classList.add('native-app')
  document.body.classList.add('native-app')

  // iOS only: `viewport-fit=cover` is what makes env(safe-area-inset-*) report the notch and
  // home indicator there — without it those values stay 0. It stays a runtime tag instead of a
  // static one in the layouts, because in a mobile browser it would put landscape content
  // under the notch.
  //
  // Android deliberately does NOT get it: the meta is what selects Capacitor's inset
  // passthrough branch (SystemBars, WebView >= 140), and that branch adds the keyboard inset a
  // second time on Android <= 14, where the framework already resizes the window — the very
  // keyboard bug this handling is meant to fix. Without the meta, Android pads the WebView
  // natively either way: today the app ships `SystemBars.insetsHandling: disable` plus a native
  // edge-to-edge plugin doing the padding, so no --safe-area-inset-* variables are written at
  // all and var() falls back to env(), which is 0 there; once that plugin is dropped,
  // Capacitor's own non-passthrough branch pads the WebView and publishes zeroed variables.
  // Either way the native.scss rules add nothing on Android.
  // REMOVE THIS PLATFORM BRANCH once the app ships a Capacitor Android version containing the
  // fix from https://github.com/ionic-team/capacitor/pull/8528 — then set the meta on Android
  // as well and the var(--safe-area-inset-*) rules take over there too.
  if (Capacitor.getPlatform() === 'ios') {
    const viewport = document.querySelector('meta[name="viewport"]')

    if (viewport) {
      let content = viewport.getAttribute('content') || 'width=device-width, initial-scale=1'

      if (!content.includes('viewport-fit=cover')) {
        content += ', viewport-fit=cover'
      }

      // WebKit zooms into a focused input whose font-size is below 16px (Membergy inputs
      // inherit 14px), shifting the view far enough to make forms partly unusable.
      // `maximum-scale=1` suppresses that zoom in the WKWebView. Pinch zoom inside the native
      // iOS app is deliberately given up for it; browsers and Android stay untouched, because
      // this only runs on the native iOS platform.
      if (!content.includes('maximum-scale')) {
        content += ', maximum-scale=1'
      }

      viewport.setAttribute('content', content)
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyNativeAppTags)
} else {
  applyNativeAppTags()
}

// A wire:navigate visit swaps <body> and strips attributes off <html> that the new page does
// not carry, so the classes and the meta addition above are gone until the next full reload.
// The function is idempotent (class add, meta checked with `includes`), so re-running it is safe.
document.addEventListener('livewire:navigated', applyNativeAppTags)
